/**
 * Kontrola korekt SFD - usluga API (kontrakt: scratchpad/kontrola/KONTRAKT-KOREKTY.md,
 * sekcja Backend pkt 1-6). Wszystko @require_admin po stronie backendu.
 *
 * Odpowiedzi ida przez success_response {success, data, message} - funkcje
 * zwracaja juz odpakowane `data`. Bledy: interceptor z api.js rzuca Error
 * z polami `status`, `errorCode`, `data` (np. 400 z komunikatem z create_correction,
 * 409 duplikat numeru FSK).
 *
 * Trasy z kontraktu: GET /kontrola-korekt/podsumowanie, GET /kontrola-korekt/lista,
 * GET /kontrola-korekt/<zrodlo_id>, POST /kontrola-korekt/pozycje/pobierz,
 * POST /kontrola-korekt/<zrodlo_id>/faktura. Dla dodaj/pomin/przywroc/przelicz kontrakt
 * nie podaje sciezek - przyjeto POST /kontrola-korekt/<zrodlo_id>/{dodaj|pomin|przywroc|przelicz}
 * i POST /kontrola-korekt/przelicz (wszystkie). Portal/pdftotext trwaja - apiLong tam,
 * gdzie backend idzie do SFD albo tworzy korekte z WZ.
 * PDF (2026-09-13): GET /kontrola-faktur/<zrodlo_id>/plik/pdf (podglad, ta sama kolumna co PDF z maila)
 * i POST /kontrola-korekt/<zrodlo_id>/pdf-z-portalu (wydruk korekty z portalu, gdy nie ma zadnego pliku).
 */
import api, { apiLong } from './api';

const odpakuj = (response) => response?.data?.data ?? response?.data;

export const kontrolaKorektService = {
  /**
   * GET /kontrola-korekt/podsumowanie?od&do&sklep_id
   * -> {liczby: {wszystkie, do_dodania, brak_faktury, faktura_poza_systemem, brak_produktow, dodane, pominiete, bez_pozycji},
   *     brutto_do_dodania, per_sklep: [{sklep_id, nazwa, wszystkie, do_dodania, dodane, brutto}], ostatnie_pobranie_pozycji}
   */
  async podsumowanie(params = {}) {
    const response = await api.get('/kontrola-korekt/podsumowanie', { params });
    return odpakuj(response);
  },

  /**
   * GET /kontrola-korekt/lista?filtr&od&do&sklep_id&szukaj&strona&na_strone
   * filtr: do_dodania|brak_faktury|faktura_poza_systemem|brak_produktow|dodane|pominiete|wszystkie
   * -> {wiersze: [...], razem, strona}
   */
  async lista(params = {}) {
    const response = await api.get('/kontrola-korekt/lista', { params });
    return odpakuj(response);
  },

  /**
   * GET /kontrola-korekt/<zrodlo_id>
   * -> {pozycje: [{lp, kod_sfd, nazwa, ilosc, cena_netto, vat, netto, brutto, produkt|null, pozycja_faktury|null, ostrzezenie}],
   *     faktura (naglowek + pozycje z systemu), kandydaci, brakujace, status, komunikat}
   */
  async szczegoly(zrodloId) {
    const response = await api.get(`/kontrola-korekt/${zrodloId}`);
    return odpakuj(response);
  },

  /**
   * POST /kontrola-korekt/pozycje/pobierz -> 202 {status:'w_toku', ...} albo synchronicznie {pobrano, pozostalo}
   * (portal b2b.sfd.pl, do 30 korekt na wywolanie).
   */
  async pobierzPozycje() {
    const response = await apiLong.post('/kontrola-korekt/pozycje/pobierz', {});
    return odpakuj(response);
  },

  /** GET /kontrola-korekt/pozycje/status -> {w_toku, status: 'w_toku'|'gotowe'|'blad'|'brak', wynik, blad} */
  async pozycjeStatus() {
    const response = await api.get('/kontrola-korekt/pozycje/status');
    return odpakuj(response);
  },

  /**
   * Przelicz (ustal_fakture + przygotuj + status): jedna korekta -> POST /kontrola-korekt/<zrodlo_id>/przelicz,
   * bez id -> POST /kontrola-korekt/przelicz (wszystkie) -> {przeliczono, ...}
   */
  async przelicz(zrodloId = null) {
    const url = zrodloId === null || zrodloId === undefined ? '/kontrola-korekt/przelicz' : `/kontrola-korekt/${zrodloId}/przelicz`;
    const response = await apiLong.post(url, {});
    return odpakuj(response);
  },

  /**
   * POST /kontrola-korekt/<zrodlo_id>/faktura body {faktura_id, wymus?}
   * 400 gdy sklep faktury != sklep korekty (chyba ze wymus: true) - err.data.komunikat.
   */
  async wybierzFakture(zrodloId, fakturaId, wymus = false) {
    const body = { faktura_id: Number(fakturaId) };
    if (wymus) body.wymus = true;
    const response = await api.post(`/kontrola-korekt/${zrodloId}/faktura`, body);
    return odpakuj(response);
  },

  /**
   * POST /kontrola-korekt/<zrodlo_id>/dodaj -> {korekta_id, korekta_numer, wz_numer, ostrzezenie|null}
   * 400 gdy status != 'gotowa' albo create_correction odrzucil (np. zwrot > przyjeto); 409 duplikat numeru FSK.
   */
  async dodaj(zrodloId) {
    const response = await apiLong.post(`/kontrola-korekt/${zrodloId}/dodaj`, {});
    return odpakuj(response);
  },

  /** POST /kontrola-korekt/<zrodlo_id>/pomin body {powod} */
  async pomin(zrodloId, powod = '') {
    const response = await api.post(`/kontrola-korekt/${zrodloId}/pomin`, { powod: powod || '' });
    return odpakuj(response);
  },

  /**
   * GET /kontrola-faktur/<zrodlo_id>/plik/pdf -> Blob (application/pdf): PDF korekty obok wiersza
   * (z maila albo wydruk z portalu) - zrodlo_id korekty = kontrola_faktur_zrodlo.id, ta sama trasa co w kontroli faktur.
   */
  async pobierzPdf(zrodloId) {
    const response = await api.get(`/kontrola-faktur/${zrodloId}/plik/pdf`, { responseType: 'blob' });
    return new Blob([response.data], { type: 'application/pdf' });
  },

  /**
   * POST /kontrola-korekt/<zrodlo_id>/pdf-z-portalu -> szczegoly korekty po pobraniu wydruku PDF z portalu
   * (plik obok wiersza, przeliczenie: numer faktury korygowanej z PDF). Portal trwa (login) - apiLong.
   * 502 SFD_PORTAL / SFD_LOGOWANIE, 400 SFD_BRAK_KONFIGURACJI - err.data.komunikat.
   */
  async pobierzPdfZPortalu(zrodloId) {
    const response = await apiLong.post(`/kontrola-korekt/${zrodloId}/pdf-z-portalu`, {});
    return odpakuj(response);
  },

  /** POST /kontrola-korekt/<zrodlo_id>/przywroc */
  async przywroc(zrodloId) {
    const response = await api.post(`/kontrola-korekt/${zrodloId}/przywroc`, {});
    return odpakuj(response);
  },
};

export default kontrolaKorektService;
